/**
 * Keeps the cart in sync with the cfy payment-option selection, with no page reload:
 *
 *   • Cash on Delivery  → applies the COD discount code (window.CFY_COD_DISCOUNT_CODE)
 *   • Prepaid options   → removes that COD code (the app applies its own prepaid discount)
 *
 * After syncing, the theme's cart totals (Subtotal / Discount / Estimated total) are
 * refreshed live. Only `.cart__summary-totals` is swapped, so the app's payment widget
 * (a sibling) is preserved.
 *
 * Discount codes are applied the same way the theme's own cart-discount field does:
 * POST /cart/update.js with a `discount` list, so they carry through to checkout.
 */
(function () {
  const RADIO_SELECTOR = 'input[name^="cfy_payment_intent"]';
  const COD_INTENT = 'manual'; // the app's value for "Cash on Delivery"
  let busy = false;
  let syncQueued = false;

  function codCode() {
    return (window.CFY_COD_DISCOUNT_CODE || '').trim();
  }

  async function getCart() {
    try {
      return await (await fetch('/cart.js', { cache: 'no-store' })).json();
    } catch (e) {
      return null;
    }
  }

  function selectedIntent() {
    const checked = document.querySelector(RADIO_SELECTOR + ':checked');
    return checked ? checked.value : null;
  }

  /**
   * Sets the cart's discount codes to exactly `codes` (Shopify's `discount` field
   * replaces the current code list; it does NOT touch automatic discounts).
   * @param {string[]} codes
   * @returns {Promise<object|null>} the /cart/update.js response
   */
  async function setDiscountCodes(codes) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(',') }),
      });
      return await res.json();
    } catch (e) {
      console.error('[cfy-cart-refresh] failed to update discount codes', e);
      return null;
    }
  }

  /** @returns {Promise<string[]>} currently applied discount codes */
  async function currentCodes() {
    // Prefer the theme's rendered discount pills so we don't wipe other codes.
    const fromDom = Array.from(document.querySelectorAll('.cart-discount__pill[data-discount-code]'))
      .map((el) => el.getAttribute('data-discount-code'))
      .filter(Boolean);
    if (fromDom.length) return [...new Set(fromDom)];

    const cart = await getCart();
    if (cart && Array.isArray(cart.discount_codes)) {
      return cart.discount_codes.map((d) => d.code);
    }
    return [];
  }

  async function refreshSummaries() {
    const summaries = document.querySelectorAll('.cart__summary-totals');
    for (const totals of summaries) {
      const section = totals.closest("[id^='shopify-section-']");
      if (!section) continue;
      const id = section.id.replace('shopify-section-', '');
      try {
        const res = await fetch(location.pathname + '?section_id=' + encodeURIComponent(id), {
          cache: 'no-store',
        });
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('.cart__summary-totals');
        const current = section.querySelector('.cart__summary-totals');
        if (fresh && current) current.replaceWith(fresh);
      } catch (e) {
        console.error('[cfy-cart-refresh] failed to refresh summary', e);
      }
    }
  }

async function refreshFreeShippingBars() {
  const bars = document.querySelectorAll('free-shipping-bar');

  for (const bar of bars) {
    const section = bar.closest("[id^='shopify-section-']");
    if (!section) continue;

    const sectionId = section.id.replace('shopify-section-', '');

    try {
      const res = await fetch(
        location.pathname +
          '?section_id=' +
          encodeURIComponent(sectionId) +
          '&_=' +
          Date.now(),
        {
          cache: 'no-store',
        }
      );

      if (!res.ok) continue;

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const freshBar = doc.querySelector('free-shipping-bar');
      const currentBar = section.querySelector('free-shipping-bar');

      if (freshBar && currentBar) {
        currentBar.replaceWith(freshBar);
      }
    } catch (error) {
      console.error(
        '[cfy-cart-refresh] failed to refresh free shipping bar',
        error
      );
    }
  }
}

async function sync() {
  if (busy) {
    syncQueued = true;
    return;
  }

  busy = true;

  try {
    do {
      syncQueued = false;

      const intent = selectedIntent();
      const code = codCode();

      if (code && intent) {
        const codes = await currentCodes();
        const has = codes.includes(code);

        if (intent === COD_INTENT && !has) {
          // COD selected → apply COD discount
          const data = await setDiscountCodes([...codes, code]);

          console.log('[CFY] COD discount response:', data);

          const entry =
            data &&
            Array.isArray(data.discount_codes)
              ? data.discount_codes.find((d) => d.code === code)
              : null;

          if (entry && entry.applicable === false) {
            console.warn(
              '[CFY] COD discount is not applicable:',
              code
            );
          }
        } else if (intent !== COD_INTENT && has) {
          // Prepaid selected → remove COD discount
          await setDiscountCodes(
            codes.filter((c) => c !== code)
          );
        }
      }

      // Give Shopify/CFY a moment to finish cart processing
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Refresh totals
      await refreshSummaries();

      // Refresh free-shipping bar
      await refreshFreeShippingBars();

    } while (syncQueued);

  } catch (error) {
    console.error('[cfy-cart-refresh] sync failed:', error);
  } finally {
    busy = false;
  }
}

  // The app's own signal that it changed the payment attribute / discount.
  document.addEventListener('cfy:cart-attribute-updated', sync);

  // Fallback / primary: react to the payment-option radio itself — no button needed.
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target && target.matches && target.matches(RADIO_SELECTOR)) sync();
  });
})();